import React, { useMemo, useState } from "react";
import { Anchor, Group, Paper, SimpleGrid, Skeleton, Text } from "@mantine/core";
import { IconArrowDownRight, IconArrowUpRight } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { aggregateOrgTestCounts } from "@utils/UIUtils";
import DemoAccountsModal from "./DemoAccountsModal";
import {
  DemoTestTotals,
  DistributorSalesData,
  OrgTestCountAnalytics,
  TimePeriod,
} from "types/analyticsTypes";

interface AnalyticsKpiCardsProps {
  testCounts: OrgTestCountAnalytics[] | null;
  sales: DistributorSalesData | null;
  demoTotals: DemoTestTotals | null;
  period: TimePeriod;
  interactive?: boolean;
}

const PERIOD_LABEL_KEYS: Record<TimePeriod, string> = {
  [TimePeriod.WEEK]: "analytics.common.testCounts.last7DaysDescription",
  [TimePeriod.MONTH]: "analytics.common.testCounts.last30DaysDescription",
  [TimePeriod.QUARTER]: "analytics.common.testCounts.last90DaysDescription",
  [TimePeriod.YEAR]: "analytics.common.testCounts.last365DaysDescription",
  [TimePeriod.RANGE]: "analytics.common.testCounts.selectedRange",
  [TimePeriod.ALL]: "analytics.kpi.allTime",
};

const KpiCard: React.FC<{
  label: string;
  value: number | null;
  sublabel?: string;
  percentageChange?: number;
  previousCount?: number;
  action?: { label: string; onClick: () => void };
}> = ({ label, value, sublabel, percentageChange, previousCount, action }) => {
  const showChange = percentageChange !== undefined && previousCount !== undefined;
  const isPositive = showChange && percentageChange > 0 && previousCount > 0;
  const isNeutral = showChange && (percentageChange === 0 || previousCount === 0);
  const changeColor = isPositive ? "green" : isNeutral ? "gray" : "orange";
  const ChangeIcon = isPositive ? IconArrowUpRight : IconArrowDownRight;

  return (
    <Paper withBorder p="md" radius="md">
      <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
        {label}
      </Text>
      <Group align="baseline" gap="xs" mt={4}>
        <Text fz={28} fw={700} lh={1.2}>
          {value === null ? "–" : value.toLocaleString()}
        </Text>
        {showChange && !isNeutral && (
          <Group gap={2} align="center" c={changeColor}>
            <ChangeIcon size={16} />
            <Text size="sm" fw={600}>
              {Math.abs(percentageChange)}%
            </Text>
          </Group>
        )}
      </Group>
      {sublabel && (
        <Text size="xs" c="dimmed" mt={2}>
          {sublabel}
        </Text>
      )}
      {action && (
        <Anchor component="button" type="button" size="xs" mt={4} onClick={action.onClick}>
          {action.label}
        </Anchor>
      )}
    </Paper>
  );
};

const AnalyticsKpiCards: React.FC<AnalyticsKpiCardsProps> = ({
  testCounts,
  sales,
  demoTotals,
  period,
  interactive = false,
}) => {
  const { t } = useTranslation();
  const [demoAccountsOpen, setDemoAccountsOpen] = useState(false);
  const demoAccounts = demoTotals?.accounts ?? [];

  const testStats = useMemo(
    () => aggregateOrgTestCounts(testCounts ?? [], period),
    [testCounts, period],
  );
  const salesTotals = useMemo(
    () =>
      (sales?.monthly ?? []).reduce(
        (totals, bucket) => ({
          newAccounts: totals.newAccounts + bucket.newAccounts,
          devicesSold: totals.devicesSold + bucket.devicesSold,
        }),
        { newAccounts: 0, devicesSold: 0 },
      ),
    [sales?.monthly],
  );

  const salesSublabel =
    period === TimePeriod.RANGE
      ? t("analytics.common.testCounts.selectedRange")
      : t("analytics.kpi.allTime");

  return (
    <Skeleton visible={!testCounts || !sales}>
      <SimpleGrid cols={{ base: 2, md: 3, lg: 5 }} spacing="md">
        <KpiCard
          label={t("analytics.kpi.completedTests")}
          value={testStats.count}
          sublabel={t(PERIOD_LABEL_KEYS[period])}
          percentageChange={testStats.percentageChange}
          previousCount={testStats.previousCount}
        />
        <KpiCard
          label={t("analytics.kpi.totalAccountCount")}
          value={salesTotals.newAccounts}
          sublabel={salesSublabel}
        />
        <KpiCard
          label={t("analytics.kpi.uniqueDevicesRegistered")}
          value={salesTotals.devicesSold}
          sublabel={salesSublabel}
        />
        <KpiCard
          label={t("analytics.kpi.totalDemosToDate")}
          value={demoTotals ? demoTotals.demoTests : null}
          sublabel={
            demoTotals
              ? t("analytics.kpi.totalTestsAllAccounts", {
                  total: demoTotals.totalTests.toLocaleString(),
                })
              : undefined
          }
          action={
            interactive && demoTotals && demoAccounts.length > 0
              ? {
                  label: t("analytics.kpi.viewDemoAccounts", { count: demoAccounts.length }),
                  onClick: () => setDemoAccountsOpen(true),
                }
              : undefined
          }
        />
        <KpiCard label={t("analytics.kpi.activeOrganizations")} value={testCounts?.length ?? 0} />
      </SimpleGrid>
      {interactive && (
        <DemoAccountsModal
          opened={demoAccountsOpen}
          onClose={() => setDemoAccountsOpen(false)}
          accounts={demoAccounts}
        />
      )}
    </Skeleton>
  );
};

export default AnalyticsKpiCards;
